// src/components/map/VehicleMap.tsx

import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader
} from '@react-google-maps/api';
import React, { useEffect, useRef } from 'react';
import { VehicleInfo } from '../../../types/vehicle.type';
import { generateColoredMapPinSvgUrlWithAlert } from '../../../utils/common.utils';
import VehicleCard from '../../vehicleCard/VehicleCard';
import './vehicleMap.scss';
import { GOOGLE_MAPS_API_KEY } from '../../../constants/config';

const containerStyle = {
  width: '100%',
  height: '100%',
};

// 👇 Updated mapStyle to remove POIs completely
const mapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#012b3c' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5e9da2' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#00151d' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#004d5c' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3399cc' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }], // Hide all Points of Interest
  },
  {
    featureType: 'poi.park',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#026a87' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#0288a7' }],
  },
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }], // Hide transit icons
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#011e2e' }],
  },
   {
    featureType: 'road',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }], // 👈 Hides all street/road names
  },
];

type Props = {
  vehicleList: VehicleInfo[];
  activeMarkerVehicleOnMapProp: VehicleInfo | null;
  onClickMarker: (v: VehicleInfo | null) => void;
  onVehicleCardMoreInfoClick: (v: VehicleInfo) => void;
  activeAlert?: {
    vehicleId: string;
    type: string;
    severity: string;
    timestamp: string;
  } | null;
};

const VehicleMap: React.FC<Props> = ({
  vehicleList,
  activeMarkerVehicleOnMapProp,
  onClickMarker,
  onVehicleCardMoreInfoClick,
  activeAlert,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (mapRef.current && activeMarkerVehicleOnMapProp) {
      const { lat, lng } = activeMarkerVehicleOnMapProp.vehicleBasicInfo;
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(15);
    }
  }, [activeMarkerVehicleOnMapProp]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Moving: '#5DF46C',
      Idle: '#E8F45D',
      Stopped: '#D50000',
      Alerts: '#FF3030',
      Maintenance: '#FF6B00',
      Offline: '#9A9A9A',
      Parked: '#00B2FF',
    };
    return colors[status] || '#999';
  };

  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={{ lat: 24.5131, lng: 54.752 }}
      zoom={13}
      options={{
        styles: mapStyle,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        zoomControl: true,
        clickableIcons: false, // 👈 disables place popups
        disableDefaultUI: false,
      }}
      onLoad={(map) => {
        mapRef.current = map;
      }}
    >
      {vehicleList.map((v) => {
        const showAlertInfo = activeAlert && activeAlert.vehicleId === v.vehicleId;
        return (
          <Marker
            key={v.vehicleId}
            position={{
              lat: v.vehicleBasicInfo.lat,
              lng: v.vehicleBasicInfo.lng,
            }}
            onClick={() => onClickMarker(v)}
            icon={generateColoredMapPinSvgUrlWithAlert(
              getStatusColor(v.vehicleBasicInfo.status),
              !!v.recentAlert
            )}
          >
            {(activeMarkerVehicleOnMapProp?.vehicleId === v?.vehicleId || showAlertInfo) && (
              <InfoWindow
                onCloseClick={() => onClickMarker(null)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -30),
                  disableAutoPan: true,
                }}
              >
                <div style={{ maxWidth: '320px' }}>
                  {showAlertInfo && activeAlert ? (
                    <div className="alert-item expanded" style={{ width: 320 }}>
                      <div className="alert-title">
                        {activeAlert.type}
                        <span className={`alert-badge ${activeAlert.severity === 'CRITICAL' ? 'critical' : 'minor'}`}>{activeAlert.severity}</span>
                      </div>
                      <div className="alert-vehicle">{v.vehicleBasicInfo.vehicle}</div>
                      <div className="alert-time" style={{color:'#9B9C9E'}}>{new Date(activeAlert.timestamp).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                      <div className="alert-details">
                        <div className="alert-detail-row" style={{fontSize:'14px', color:'#B0B0B0'}} >Device ID: {v.vehicleBasicInfo.deviceId}</div>
                        <div className="alert-detail-row" style={{fontSize:'14px', color:'#B0B0B0'}} >License Plate No.: {v.vehicleBasicInfo.licensePlate}</div>
                        <div className="alert-detail-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={`status-dot`} style={{ background: getStatusColor(v.vehicleBasicInfo.status) || '#999', width: 12, height: 12, borderRadius: '50%', display: 'inline-block' }}></span>
                          <span style={{ color: getStatusColor(v.vehicleBasicInfo.status) || '#fff', fontWeight: 600, fontSize:'12px' }}>{v.vehicleBasicInfo.status}</span>
                          <span style={{ display: 'flex', alignItems: 'center', marginLeft: 12, fontSize:'12px' }}>
                            <img src={require('../../../assets/svgs/map-pin.icon.svg').default} alt="Location" style={{ width: 16, marginRight: 4 }} />
                            {v.vehicleBasicInfo.location}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <VehicleCard
                      vehicleId={v.vehicleId}
                      vehicleBasicInfo={v.vehicleBasicInfo}
                      vehicleDetailInfo={v.vehicleDetailInfo}
                      expandMetrics={true}
                      onMoreInfoClick={() => onVehicleCardMoreInfoClick(v)}
                    />
                  )}
                </div>
              </InfoWindow>
            )}
          </Marker>
        );
      })}
    </GoogleMap>
  );
};

export default VehicleMap;
